/**
 * Rules Store
 * State management for parameter-driven compliance validation system
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { complianceDataService } from "../services/core/complianceDataService";
import { loadComplianceReport, loadValidationReportsFromManifest } from "../services/dataService";
import type { ADASideWallGrabBarReport } from "../types/adaSideWallGrabBar";
import { DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS } from "../types/adaSideWallGrabBar";
import type { ADAToiletValidationReport } from "../types/adaToiletRule";
import { ADA_TOILET_DEFAULTS } from "../types/adaToiletRule";
import type { FireAlarmValidationReport } from "../types/simpleFireAlarm";
import { DEFAULT_FIRE_ALARM_PARAMS } from "../types/simpleFireAlarm";
import type {
  RuleInstance,
  RuleTemplate,
  RuleValidationSummary,
  StoredRuleConfiguration,
  ValidationResult,
  User,
  IssueStatus,
  IssuePriority,
} from "../types/rules";
import { useViewerStore } from "./viewerStore";
import { issuesClient } from "../api/clients/issues-client";
import { usersClient } from "../api/clients/users-client";
import type { TeamMember } from "../types/users";

// Initial Rule Templates
const ruleTemplates: RuleTemplate[] = [
  {
    id: "ada-toilet-side-wall-clearance",
    name: "ADA Toilet Side Wall Clearance (IBC 604.2)",
    category: "IBC 604.2- ADA toilet side wall",
    description:
      'Validates that ADA toilet centerline is 16" minimum to 18" maximum from the side wall per IBC 604.2 requirements.',
    standard: "IBC 604.2",
    elementTypes: ["Toilets", "Plumbing Fixtures"],
    parameters: [
      {
        id: "adaIdentifier",
        label: "ADA Toilet Identifier",
        type: "string",
        value: ADA_TOILET_DEFAULTS.adaIdentifier,
        placeholder: "e.g., Unhousted1, ADA",
        description:
          "Text string to identify ADA toilets (case-insensitive contains match)",
      },
      {
        id: "minDistance",
        label: "Minimum Distance",
        type: "number",
        value: ADA_TOILET_DEFAULTS.minDistance,
        unit: "inches",
        min: 12,
        max: 24,
        description:
          "Minimum required distance from toilet centerline to side wall (inches)",
      },
      {
        id: "maxDistance",
        label: "Maximum Distance",
        type: "number",
        value: ADA_TOILET_DEFAULTS.maxDistance,
        unit: "inches",
        min: 12,
        max: 24,
        description:
          "Maximum allowed distance from toilet centerline to side wall (inches)",
      },
      {
        id: "warningZone",
        label: "Warning Zone",
        type: "number",
        value: ADA_TOILET_DEFAULTS.warningZone,
        unit: "inches",
        min: 0,
        max: 2,
        step: 0.1,
        description:
          "Tolerance value for warning zone at min/max boundaries (inches)",
      },
    ],
  },
  {
    id: "ada-side-wall-grab-bar",
    name: "ADA Side Wall Grab Bar (604.5.1)",
    category: "ADA 604.5.1- Side wall grab bar",
    description:
      'Validates that side wall grab bars are 42" minimum long, located 12" maximum from rear wall, and extend 54" minimum from rear wall.',
    standard: "ADA 604.5.1",
    elementTypes: ["Grab Bars", "Specialty Equipment"],
    parameters: [
      {
        id: "grabBarIdentifier",
        label: "Grab Bar Identifier",
        type: "string",
        value: DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS.grabBarIdentifier,
        placeholder: "e.g., Side Grab Bar-3D2",
        description:
          "Text string to identify grab bars (case-insensitive contains match)",
      },
      {
        id: "adaIdentifier",
        label: "ADA Toilet Identifier",
        type: "string",
        value: DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS.adaIdentifier,
        placeholder: "e.g., ada, ADA",
        description:
          "Text string to identify ADA toilets (case-insensitive contains match)",
      },
      {
        id: "minLength",
        label: "Minimum Length",
        type: "number",
        value: DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS.minLength,
        unit: "inches",
        min: 36,
        max: 48,
        description: "Minimum required length of grab bar (inches)",
      },
      {
        id: "maxDistanceFromRearWall",
        label: "Max Distance from Rear Wall",
        type: "number",
        value: DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS.maxDistanceFromRearWall,
        unit: "inches",
        min: 6,
        max: 18,
        description:
          "Maximum allowed distance from rear wall to nearest end (inches)",
      },
      {
        id: "minExtensionFromRearWall",
        label: "Min Extension from Rear Wall",
        type: "number",
        value: DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS.minExtensionFromRearWall,
        unit: "inches",
        min: 48,
        max: 60,
        description:
          "Minimum required extension from rear wall to furthest end (inches)",
      },
      {
        id: "raycastDistance",
        label: "Ray Cast Distance",
        type: "number",
        value: DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS.raycastDistance,
        unit: "feet",
        min: 1,
        max: 10,
        step: 0.5,
        description: "Maximum distance to search for walls (feet)",
      },
      {
        id: "measurementTolerance",
        label: "Measurement Tolerance",
        type: "number",
        value: DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS.measurementTolerance,
        unit: "inches",
        min: 0,
        max: 1,
        step: 0.1,
        description: "Tolerance for measurements (inches)",
      },
    ],
  },
  {
    id: "fire-alarm-visual-clearance",
    name: "NFPA 72 - FBC: Fire Safety - visual alarms",
    category: "NFPA 72 - Fire Safety",
    description:
      "Validates that visual fire alarm devices have adequate clearance for installation and service access per NFPA 72 requirements.",
    standard: "NFPA 72",
    elementTypes: ["Fire Alarm Devices", "Visual Alarms"],
    parameters: [
      {
        id: "deviceCategory",
        label: "Device Category",
        type: "string",
        value: DEFAULT_FIRE_ALARM_PARAMS.deviceCategory,
        placeholder: "e.g., Revit Fire Alarm Devices",
        description: "Revit category containing fire alarm devices",
      },
      {
        id: "deviceFamilyFilter",
        label: "Device Family Filter",
        type: "string",
        value: DEFAULT_FIRE_ALARM_PARAMS.deviceFamilyFilter,
        placeholder: "e.g., AEC_Indoor Horns, Strobes",
        description: "Filter devices by family name (partial match)",
      },
      {
        id: "maxRayDistance",
        label: "Max Ray Distance",
        type: "number",
        value: DEFAULT_FIRE_ALARM_PARAMS.maxRayDistance,
        unit: "feet",
        min: 5,
        max: 100,
        description: "Maximum distance for ray casting to detect walls",
      },
      // demoMode is now always true by default, no UI control needed
      // Removed performClearanceAnalysis checkbox - analysis is now always enabled
    ],
  },
];

export interface RulesState {
  // Rule Configuration
  ruleTemplates: RuleTemplate[];
  ruleInstances: RuleInstance[];

  // Custom Rules Configuration
  customRuleTemplates: RuleTemplate[];
  customRuleInstances: RuleInstance[];

  // Validation Results
  validationResults: ValidationResult[];
  validationSummary: RuleValidationSummary | null;
  adaToiletValidationReport: ADAToiletValidationReport | null;
  adaSideWallGrabBarReport: ADASideWallGrabBarReport | null;
  fireAlarmValidationReport: FireAlarmValidationReport | null;

  // Issue Management
  availableUsers: TeamMember[];
  customUsers: TeamMember[]; // Up to 3 optional users that can be added
  supportUser: TeamMember; // Permanent support user (non-removable)
  currentUser: User | null;
  issueStats: any | null;

  // UI State
  isValidating: boolean;
  validationProgress: number;
  validationProgressText: string;
  selectedRuleId: string | null;

  // Saved Configurations
  savedConfigurations: StoredRuleConfiguration[];
  activeConfigurationName: string | null;

  // Actions - Rule Management
  toggleRule: (templateId: string) => void;
  updateRuleParameter: (
    templateId: string,
    paramId: string,
    value: any
  ) => void;
  resetRuleParameters: (templateId: string) => void;
  loadRuleDescriptions: () => Promise<void>;

  // Actions - Custom Rule Management
  addCustomRule: (template: RuleTemplate) => void;
  updateCustomRule: (
    templateId: string,
    template: Partial<RuleTemplate>
  ) => void;
  deleteCustomRule: (templateId: string) => void;
  toggleCustomRule: (templateId: string) => void;
  updateCustomRuleParameter: (
    templateId: string,
    paramId: string,
    value: any
  ) => void;
  resetCustomRuleParameters: (templateId: string) => void;

  // Actions - Validation
  runValidation: () => Promise<void>;
  setValidationResults: (results: ValidationResult[]) => void;
  setValidationSummary: (summary: RuleValidationSummary) => void;
  setADAToiletValidationReport: (
    report: ADAToiletValidationReport | null
  ) => void;
  setADASideWallGrabBarReport: (
    report: ADASideWallGrabBarReport | null
  ) => void;

  // Actions - Issue Management
  loadUsers: () => Promise<void>;
  addCustomUser: (user: Omit<TeamMember, "id" | "joinedAt">) => void;
  removeCustomUser: (userId: string) => void;
  setCurrentUser: (user: User | null) => void;
  assignIssue: (issueId: string, user: User | null) => Promise<void>;
  updateIssueStatus: (issueId: string, status: IssueStatus) => Promise<void>;
  updateIssuePriority: (
    issueId: string,
    priority: IssuePriority
  ) => Promise<void>;
  setIssueDueDate: (issueId: string, dueDate: Date) => Promise<void>;
  addIssueNote: (issueId: string, text: string) => Promise<void>;
  resolveIssue: (issueId: string, resolutionNote?: string) => Promise<void>;
  saveIssuesToBackend: () => Promise<void>;
  loadIssuesFromBackend: () => Promise<void>;
  refreshIssueStats: () => Promise<void>;
  setIssueFinancialImpact: (
    issueId: string,
    impact: number | null
  ) => Promise<void>;
  setFireAlarmValidationReport: (
    report: FireAlarmValidationReport | null
  ) => void;
  clearValidationResults: () => void;

  // Actions - UI State
  setIsValidating: (isValidating: boolean) => void;
  setValidationProgress: (progress: number, text?: string) => void;
  setSelectedRuleId: (ruleId: string | null) => void;

  // Actions - Configuration Management
  saveConfiguration: (name: string, description?: string) => void;
  loadConfiguration: (name: string) => void;
  deleteConfiguration: (name: string) => void;

  // Utility Actions
  getActiveRules: () => RuleInstance[];
  getRuleInstance: (templateId: string) => RuleInstance | undefined;
  getActiveCustomRules: () => RuleInstance[];
  getCustomRuleInstance: (templateId: string) => RuleInstance | undefined;
}

// Initialize rule instances from templates
const initializeRuleInstances = (): RuleInstance[] => {
  return ruleTemplates.map((template) => ({
    templateId: template.id,
    isActive: true,
    parameters: [...template.parameters],
    lastModified: new Date(),
  }));
};

// Helper to sync rule instances with templates (handles new rules being added)
const syncRuleInstances = (
  existingInstances: RuleInstance[]
): RuleInstance[] => {
  const synced: RuleInstance[] = [];

  // Add all rules from templates
  ruleTemplates.forEach((template) => {
    const existing = existingInstances.find(
      (i) => i.templateId === template.id
    );
    if (existing) {
      // Merge existing instance with template parameters (add new parameters, keep existing values)
      const mergedParameters = template.parameters.map((templateParam) => {
        const existingParam = existing.parameters.find(
          (p) => p.id === templateParam.id
        );
        return existingParam || templateParam; // Use existing value or template default
      });

      synced.push({
        ...existing,
        parameters: mergedParameters,
        lastModified: new Date(),
      });
    } else {
      // Add new rule instance for templates not in storage
      synced.push({
        templateId: template.id,
        isActive: false,
        parameters: [...template.parameters],
        lastModified: new Date(),
      });
    }
  });

  return synced;
};

export const useRulesStore = create<RulesState>()(
  persist(
    (set, get) => ({
      // Initial State
      ruleTemplates,
      ruleInstances: initializeRuleInstances(),
      customRuleTemplates: [],
      customRuleInstances: [],
      validationResults: [],
      validationSummary: null,
      adaToiletValidationReport: null,
      adaSideWallGrabBarReport: null,
      fireAlarmValidationReport: null,
      availableUsers: [],
      customUsers: [], // Populated per-model (e.g. Dutch Bros team in ModelConfiguration)
      supportUser: {
        id: "support-user",
        name: "Support Team",
        email: "elad@herocon.ai",
        role: "admin" as const,
        isActive: true,
        joinedAt: new Date(),
      }, // Permanent support user
      currentUser: {
        id: "current-user",
        name: "Current User",
        email: "user@herocon.ai",
      }, // Set default current user for assignment tracking
      issueStats: null,
      isValidating: false,
      validationProgress: 0,
      validationProgressText: "",
      selectedRuleId: null,
      savedConfigurations: [],
      activeConfigurationName: null,

      // Rule Management Actions
      toggleRule: (templateId) => {
        set((state) => ({
          ruleInstances: state.ruleInstances.map((instance) =>
            instance.templateId === templateId
              ? {
                  ...instance,
                  isActive: !instance.isActive,
                  lastModified: new Date(),
                }
              : instance
          ),
        }));
      },

      updateRuleParameter: (templateId, paramId, value) => {
        console.log("[RulesStore] Updating parameter:", {
          templateId,
          paramId,
          value,
          valueType: typeof value,
        });
        set((state) => {
          const updated = {
            ruleInstances: state.ruleInstances.map((instance) =>
              instance.templateId === templateId
                ? {
                    ...instance,
                    parameters: instance.parameters.map((param) =>
                      param.id === paramId ? { ...param, value } : param
                    ),
                    lastModified: new Date(),
                  }
                : instance
            ),
          };
          // Log the updated parameter value to verify it was saved
          const updatedInstance = updated.ruleInstances.find(
            (i) => i.templateId === templateId
          );
          if (updatedInstance) {
            const updatedParam = updatedInstance.parameters.find(
              (p) => p.id === paramId
            );
            console.log("[RulesStore] Parameter updated:", {
              paramId,
              newValue: updatedParam?.value,
              newValueType: typeof updatedParam?.value,
            });
          }
          return updated;
        });
      },

      resetRuleParameters: (templateId) => {
        const template = ruleTemplates.find((t) => t.id === templateId);
        if (!template) return;

        set((state) => ({
          ruleInstances: state.ruleInstances.map((instance) =>
            instance.templateId === templateId
              ? {
                  ...instance,
                  parameters: [...template.parameters],
                  lastModified: new Date(),
                }
              : instance
          ),
        }));
      },

      // TODO: Database Migration - Replace JSON loading with database API calls
      loadRuleDescriptions: async () => {
        try {
          const complianceData =
            await complianceDataService.loadRuleDescriptions();

          // Update rule templates with descriptions from JSON file
          const updatedTemplates = ruleTemplates.map((template) => {
            // TODO: Database Migration - Replace this mapping logic with proper rule ID indexing
            // Map template ID to compliance data rule key based on specific rules
            let ruleKey: string | null = null;

            if (template.id === "ada-toilet-side-wall-clearance") {
              ruleKey = "ADA_604.2";
            }
            // Add more mappings here as new rules are added

            const ruleData = ruleKey ? complianceData.rules[ruleKey] : null;

            if (ruleData) {
              return {
                ...template,
                name: ruleData.title,
                description: ruleData.description,
                standard: ruleData.code,
              };
            }

            return template; // Keep original if no match found
          });

          set({ ruleTemplates: updatedTemplates });
        } catch (error) {
          // Keep existing hardcoded descriptions if loading fails
        }
      },

      // Validation Actions
      runValidation: async () => {
        const {
          getActiveRules,
          getActiveCustomRules,
          setIsValidating,
          setValidationProgress,
        } = get();
        const activeRules = getActiveRules();
        const activeCustomRules = getActiveCustomRules();
        const allActiveRules = [...activeRules, ...activeCustomRules];

        if (allActiveRules.length === 0) {
          return;
        }

        setIsValidating(true);
        setValidationProgress(0, "Loading validation report...");

        const startTime = Date.now();

        try {
          // Load compliance report from single file
          setValidationProgress(10, "Loading compliance report...");

          const validReports = await loadComplianceReport<any>();

          setValidationProgress(25, "Processing regulations...");

          // Convert regulations array to ValidationResult format
          const mappingResults: ValidationResult[] = [];

          // Collect all regulations from all reports
          const allRegulations: any[] = [];
          validReports.forEach((report) => {
            // Handle v4 format (regulation_validation_results), v3 format (validation_results), and old format (regulations)
            const regulations =
              report.regulation_validation_results ||
              report.validation_results ||
              report.regulations ||
              [];
            regulations.forEach((regulation: any) => {
              // Filter by visible field - only include regulations where visible === true
              if (regulation.visible !== false) {
                allRegulations.push(regulation);
              }
            });
          });

          // Process regulations with simulated delay
          for (let index = 0; index < allRegulations.length; index++) {
            const regulation = allRegulations[index];
            const totalRegulations = allRegulations.length;
            const progress = 25 + (index / totalRegulations) * 40;

            // Check if this is a 1103 regulation
            const regulationId = regulation.regulation_id || "";
            const is1103 = regulationId.startsWith("1103");

            const progressText = is1103
              ? `Processing 1103 and its subchapters: ${regulation.regulation_id}...`
              : `Processing ${regulation.regulation_id}...`;

            setValidationProgress(progress, progressText);

            // Map status from validation report to ValidationResult status
            let status: "pass" | "fail" | "warning" | "info" = "warning";
            if (regulation.status === "PASS") {
              status = "pass";
            } else if (regulation.status === "FAIL") {
              status = "fail";
            } else if (
              regulation.status === "NOT_TESTED_INSUFFICIENT_DATA" ||
              regulation.status === "NOT_TESTED_MISSING_ELEMENTS"
            ) {
              status = "warning";
            } else {
              status = "info";
            }

            // Extract element ID from entities_used if available
            let elementId = 0;
            if (
              regulation.entities_used &&
              regulation.entities_used.length > 0
            ) {
              const firstEntity = regulation.entities_used[0];
              elementId = firstEntity.dbId || firstEntity.elementId || 0;
            }

            // Get standard from first valid report (all reports should have same standard)
            const standard =
              validReports.length > 0 &&
              validReports[0].validation_metadata?.standard
                ? validReports[0].validation_metadata.standard
                : "ICC A117.1-2017";

            // Create a validation result for each regulation
            mappingResults.push({
              ruleId: `ada-604-${regulation.regulation_id}`,
              ruleName: regulation.title || regulation.regulation_id,
              standard: standard,
              status: status,
              elementId: elementId,
              elementName:
                regulation.entities_used
                  ?.map((e: any) => e.entity_name)
                  .join(", ") || "Multiple entities",
              message: `${regulation.regulation_id}: ${regulation.title || "No title"}. ${regulation.measured_value ? `Measured: ${regulation.measured_value}` : ""} ${regulation.required_value ? `Required: ${regulation.required_value}` : ""}`,
              data: {
                details: {
                  regulationId: regulation.regulation_id,
                  section: regulation.section,
                  status: regulation.status,
                  measuredValue: regulation.measured_value,
                  requiredValue: regulation.required_value,
                  jerseySpecific: regulation.jersey_specific,
                  notes: regulation.notes,
                  confidence: regulation.confidence,
                  severity: regulation.severity,
                  entitiesUsed: regulation.entities_used || [],
                },
              },
              timestamp: new Date(),
            });
          }

          setValidationProgress(65, "Running validation engine...");

          // Import the validation service dynamically for any active rule validations
          const { runValidationEngine } = await import(
            "../services/validators/regulationValidators"
          );
          const viewerStore = useViewerStore.getState();

          let engineResults: ValidationResult[] = [];
          if (viewerStore.viewer) {
            // Run validation with progress callback for active rules
            // Note: Custom rules without validators will be skipped by the validation engine
            engineResults = await runValidationEngine(
              allActiveRules,
              viewerStore.viewer,
              (progress, text) => {
                // Scale progress from 65-90 for engine results
                const scaledProgress = 65 + (progress / 100) * 25;
                setValidationProgress(scaledProgress, text);
              }
            );
          }

          // Combine mapping results with engine results
          const allResults = [...mappingResults, ...engineResults];

          setValidationProgress(95, "Finalizing results...");

          // Calculate summary
          const summary: RuleValidationSummary = {
            // Total rules is the count of active rules that participated in validation
            totalRules: allActiveRules.length,
            rulesExecuted:
              allResults.length > 0
                ? new Set(allResults.map((r) => r.ruleId)).size
                : 0,
            elementsTested: new Set(allResults.map((r) => r.elementId)).size,
            violations: allResults.filter((r) => r.status === "fail").length,
            warnings: allResults.filter((r) => r.status === "warning").length,
            passes: allResults.filter((r) => r.status === "pass").length,
            executionTime: Date.now() - startTime,
            timestamp: new Date(),
          };

          get().setValidationResults(allResults);
          get().setValidationSummary(summary);
        } catch (error) {
          console.error("Validation error:", error);
        } finally {
          setIsValidating(false);
          setValidationProgress(100);
        }
      },

      setValidationResults: (results) => set({ validationResults: results }),

      setValidationSummary: (summary) => set({ validationSummary: summary }),

      setADAToiletValidationReport: (report) =>
        set({ adaToiletValidationReport: report }),

      setADASideWallGrabBarReport: (report) =>
        set({ adaSideWallGrabBarReport: report }),

      setFireAlarmValidationReport: (report) =>
        set({ fireAlarmValidationReport: report }),

      clearValidationResults: () =>
        set({
          validationResults: [],
          validationSummary: null,
          adaToiletValidationReport: null,
          adaSideWallGrabBarReport: null,
          fireAlarmValidationReport: null,
          validationProgress: 0,
        }),

      // UI State Actions
      setIsValidating: (isValidating) => set({ isValidating }),

      setValidationProgress: (progress, text) =>
        set({
          validationProgress: progress,
          validationProgressText: text || "",
        }),

      setSelectedRuleId: (ruleId) => set({ selectedRuleId: ruleId }),

      // Configuration Management
      saveConfiguration: (name, description) => {
        const { ruleInstances } = get();
        const config: StoredRuleConfiguration = {
          name,
          description,
          instances: ruleInstances,
          createdAt: new Date(),
          modifiedAt: new Date(),
        };

        set((state) => ({
          savedConfigurations: [
            ...state.savedConfigurations.filter((c) => c.name !== name),
            config,
          ],
          activeConfigurationName: name,
        }));
      },

      loadConfiguration: (name) => {
        const config = get().savedConfigurations.find((c) => c.name === name);
        if (config) {
          set({
            ruleInstances: config.instances,
            activeConfigurationName: name,
          });
        }
      },

      deleteConfiguration: (name) => {
        set((state) => ({
          savedConfigurations: state.savedConfigurations.filter(
            (c) => c.name !== name
          ),
          activeConfigurationName:
            state.activeConfigurationName === name
              ? null
              : state.activeConfigurationName,
        }));
      },

      // Issue Management Actions
      loadUsers: async () => {
        // Local-only mode - no backend
        const { customUsers, supportUser } = get();
        // Only include custom users and support user (no hardcoded mock users)
        set({ availableUsers: [supportUser, ...customUsers] });
      },

      addCustomUser: (userData) => {
        const { customUsers } = get();
        // Limit to 3 custom users
        if (customUsers.length >= 3) {
          alert("You can only add up to 3 custom users.");
          return;
        }

        const newUser: TeamMember = {
          ...userData,
          id: `custom-user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          joinedAt: new Date(),
        };

        const updatedCustomUsers = [...customUsers, newUser];
        const { availableUsers, supportUser } = get();
        // Update availableUsers to include the new custom user (keep support user)
        const nonCustomUsers = availableUsers.filter(
          (u) => !u.id.startsWith("custom-user") && u.id !== supportUser.id
        );
        set({
          customUsers: updatedCustomUsers,
          availableUsers: [
            ...nonCustomUsers,
            supportUser,
            ...updatedCustomUsers,
          ],
        });
      },

      removeCustomUser: (userId) => {
        const { customUsers, availableUsers, supportUser } = get();
        const updatedCustomUsers = customUsers.filter((u) => u.id !== userId);
        // Update availableUsers to remove the custom user and keep support user
        const nonCustomUsers = availableUsers.filter(
          (u) => !u.id.startsWith("custom-user") && u.id !== supportUser.id
        );
        set({
          customUsers: updatedCustomUsers,
          availableUsers: [
            ...nonCustomUsers,
            supportUser,
            ...updatedCustomUsers,
          ],
        });
      },

      setCurrentUser: (user) => {
        set({ currentUser: user });
      },

      assignIssue: async (issueId, user) => {
        const { currentUser, validationResults } = get();
        if (!currentUser) {
          console.error("No current user set");
          return;
        }

        try {
          const updatedIssue = await issuesClient.assignIssue(
            issueId,
            user,
            currentUser
          );

          // Update local state
          set((state) => ({
            validationResults: state.validationResults.map((result) =>
              result.issueId === issueId
                ? { ...result, ...updatedIssue }
                : result
            ),
          }));

          // Refresh stats
          get().refreshIssueStats();
        } catch (error) {
          console.error(
            "Failed to assign issue (using local fallback):",
            error
          );
          // Fallback: Update locally without backend
          set((state) => ({
            validationResults: state.validationResults.map((result) =>
              result.issueId === issueId
                ? {
                    ...result,
                    assignment: {
                      assignedTo: user || undefined,
                      assignedBy: currentUser,
                      assignedAt: new Date(),
                    },
                    issueStatus: user ? "in_progress" : "open",
                  }
                : result
            ),
          }));
        }
      },

      updateIssueStatus: async (issueId, status) => {
        // Local-only mode - update state directly
        set((state) => ({
          validationResults: state.validationResults.map((result) => {
            const isMatch =
              result.issueId === issueId ||
              (!result.issueId &&
                `issue-${result.elementId}-${result.ruleId}` === issueId);

            return isMatch
              ? {
                  ...result,
                  issueId: issueId,
                  issueStatus: status,
                }
              : result;
          }),
        }));
      },

      updateIssuePriority: async (issueId, priority) => {
        // Local-only mode - update state directly
        set((state) => ({
          validationResults: state.validationResults.map((result) => {
            const isMatch =
              result.issueId === issueId ||
              (!result.issueId &&
                `issue-${result.elementId}-${result.ruleId}` === issueId);

            return isMatch
              ? {
                  ...result,
                  issueId: issueId,
                  issuePriority: priority,
                }
              : result;
          }),
        }));
      },

      setIssueDueDate: async (issueId, dueDate) => {
        // Local-only mode - update state directly
        set((state) => ({
          validationResults: state.validationResults.map((result) =>
            result.issueId === issueId
              ? { ...result, dueDate: dueDate }
              : result
          ),
        }));
      },

      setIssueFinancialImpact: async (issueId, impact) => {
        // Local-only mode - update state directly
        set((state) => ({
          validationResults: state.validationResults.map((result) => {
            const isMatch =
              result.issueId === issueId ||
              (!result.issueId &&
                `issue-${result.elementId}-${result.ruleId}` === issueId);

            return isMatch
              ? {
                  ...result,
                  issueId: issueId,
                  financialImpact: impact === null ? undefined : impact,
                }
              : result;
          }),
        }));
      },

      addIssueNote: async (issueId, text) => {
        const { currentUser } = get();
        if (!currentUser) {
          console.error("No current user set");
          return;
        }

        // Local-only mode - update state directly
        const note = {
          id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          userId: currentUser.id,
          userName: currentUser.name,
          text,
          timestamp: new Date(),
        };

        set((state) => ({
          validationResults: state.validationResults.map((result) =>
            result.issueId === issueId
              ? {
                  ...result,
                  notes: [...(result.notes || []), note],
                }
              : result
          ),
        }));
      },

      resolveIssue: async (issueId, resolutionNote) => {
        const { currentUser } = get();
        if (!currentUser) {
          console.error("No current user set");
          return;
        }

        // Local-only mode - update state directly
        set((state) => ({
          validationResults: state.validationResults.map((result) =>
            result.issueId === issueId
              ? {
                  ...result,
                  issueStatus: "resolved",
                  resolvedAt: new Date(),
                  resolvedBy: currentUser,
                  resolutionNote: resolutionNote,
                }
              : result
          ),
        }));
      },

      saveIssuesToBackend: async () => {
        // Local-only mode - generate issue IDs for failed/warning items
        set((state) => ({
          validationResults: state.validationResults.map((result) =>
            (result.status === "fail" || result.status === "warning") &&
            !result.issueId
              ? {
                  ...result,
                  issueId: `issue-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  issueStatus: "open",
                }
              : result
          ),
        }));
      },

      loadIssuesFromBackend: async () => {
        // Local-only mode - no-op (issues are already in state)
        console.log("Local mode: Issues already loaded from validation");
      },

      refreshIssueStats: async () => {
        // Local-only mode - calculate stats from current state
        const { validationResults } = get();
        const stats = {
          total: validationResults.filter((r) => r.issueId).length,
          assigned: validationResults.filter((r) => r.assignment?.assignedTo)
            .length,
          unassigned: validationResults.filter(
            (r) => r.issueId && !r.assignment?.assignedTo
          ).length,
          byStatus: {
            open: validationResults.filter((r) => r.issueStatus === "open")
              .length,
            in_progress: validationResults.filter(
              (r) => r.issueStatus === "in_progress"
            ).length,
            resolved: validationResults.filter(
              (r) => r.issueStatus === "resolved"
            ).length,
            closed: validationResults.filter((r) => r.issueStatus === "closed")
              .length,
          },
          byPriority: {
            low: validationResults.filter((r) => r.issuePriority === "low")
              .length,
            medium: validationResults.filter(
              (r) => r.issuePriority === "medium"
            ).length,
            high: validationResults.filter((r) => r.issuePriority === "high")
              .length,
            critical: validationResults.filter(
              (r) => r.issuePriority === "critical"
            ).length,
          },
        };
        set({ issueStats: stats });
      },

      // Custom Rule Management Actions
      addCustomRule: (template) => {
        const newTemplate = {
          ...template,
          id:
            template.id ||
            `custom-rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };

        // Set isActive based on template.enabled if provided
        const newInstance: RuleInstance = {
          templateId: newTemplate.id,
          isActive: newTemplate.enabled ?? false,
          parameters: [...newTemplate.parameters],
          lastModified: new Date(),
        };

        set((state) => ({
          customRuleTemplates: [...state.customRuleTemplates, newTemplate],
          customRuleInstances: [...state.customRuleInstances, newInstance],
        }));
      },

      updateCustomRule: (templateId, updates) => {
        set((state) => ({
          customRuleTemplates: state.customRuleTemplates.map((template) =>
            template.id === templateId ? { ...template, ...updates } : template
          ),
        }));
      },

      deleteCustomRule: (templateId) => {
        set((state) => ({
          customRuleTemplates: state.customRuleTemplates.filter(
            (template) => template.id !== templateId
          ),
          customRuleInstances: state.customRuleInstances.filter(
            (instance) => instance.templateId !== templateId
          ),
        }));
      },

      toggleCustomRule: (templateId) => {
        set((state) => ({
          customRuleInstances: state.customRuleInstances.map((instance) =>
            instance.templateId === templateId
              ? {
                  ...instance,
                  isActive: !instance.isActive,
                  lastModified: new Date(),
                }
              : instance
          ),
        }));
      },

      updateCustomRuleParameter: (templateId, paramId, value) => {
        set((state) => ({
          customRuleInstances: state.customRuleInstances.map((instance) =>
            instance.templateId === templateId
              ? {
                  ...instance,
                  parameters: instance.parameters.map((param) =>
                    param.id === paramId ? { ...param, value } : param
                  ),
                  lastModified: new Date(),
                }
              : instance
          ),
        }));
      },

      resetCustomRuleParameters: (templateId) => {
        const template = get().customRuleTemplates.find(
          (t) => t.id === templateId
        );
        if (!template) return;

        set((state) => ({
          customRuleInstances: state.customRuleInstances.map((instance) =>
            instance.templateId === templateId
              ? {
                  ...instance,
                  parameters: [...template.parameters],
                  lastModified: new Date(),
                }
              : instance
          ),
        }));
      },

      // Utility Functions
      getActiveRules: () => {
        const { ruleInstances } = get();
        return ruleInstances.filter((instance) => instance.isActive);
      },

      getRuleInstance: (templateId) => {
        const { ruleInstances } = get();
        return ruleInstances.find(
          (instance) => instance.templateId === templateId
        );
      },

      getActiveCustomRules: () => {
        const { customRuleInstances } = get();
        return customRuleInstances.filter((instance) => instance.isActive);
      },

      getCustomRuleInstance: (templateId) => {
        const { customRuleInstances } = get();
        return customRuleInstances.find(
          (instance) => instance.templateId === templateId
        );
      },
    }),
    {
      name: "herocon-rules-storage",
      partialize: (state) => ({
        ruleInstances: state.ruleInstances,
        customRuleTemplates: state.customRuleTemplates,
        customRuleInstances: state.customRuleInstances,
        savedConfigurations: state.savedConfigurations,
        activeConfigurationName: state.activeConfigurationName,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Sync rule instances to handle new rules added to templates
          state.ruleInstances = syncRuleInstances(state.ruleInstances);
        }
      },
    }
  )
);
